<form [formGroup]="paymentPlanForm" (ngSubmit)="submitPaymentPlan()">
  <mat-card-content fxLayout="column">
    <div
      fxFlex
      fxLayout="row"
      fxLayoutGap="1rem"
      fxLayoutAlign="space-between center"
    >
      <mat-form-field fxFlex>
        <mat-label>{{ 'payments.charge.credit-card' | translate }}</mat-label>
        <mat-select formControlName="creditCard" required>
          <mat-select-trigger
            *ngIf="card"
            fxLayout="row"
            fxLayoutAlign="start center"
            fxLayoutGap=".5rem"
          >
            <div fxFlex>
              {{ card.lastFourDigits | creditCardNumber }}
            </div>
            <div>{{ card.expiryDate }}</div>
            <img class="credit-card-icon" [src]="card | cardImage"/>
            <span *ngIf="card.funding">{{ card.funding | mmxTitlecase }}
            </span>
          </mat-select-trigger>
          <mat-option
            *ngFor="let card of creditCards"
            [value]="card.id"
            [matTooltip]="'payments.charge.provider-by' | translate: { value: card.provider.toUpperCase() }"
          >
            <div class="card-details">
              <div fxFlex class="card-digits">{{ card.lastFourDigits | creditCardNumber }}</div>
              <div>{{ card.expiryDate }}</div>
              <img
                class="credit-card-icon"
                [src]="card | cardImage"
              />
              <span *ngIf="card.funding"
                >{{ card.funding | mmxTitlecase }}
              </span>
            </div>
          </mat-option>
          <mat-option [value]="null" (click)="openAddCardDialog()">
            <mat-icon>add</mat-icon>
            {{ 'payments.charge.add-card' | translate }}
            </mat-option
          >
        </mat-select>
        <mat-hint *ngIf="invoice.plan">
          {{ 'payments.charge.update-credit-card' | translate }}
        </mat-hint>
        <mat-error *ngIf="paymentPlanForm.controls.creditCard.invalid">{{
          getErrorMessage('cardNumber', 'Card Number')
        }}</mat-error>
      </mat-form-field>

      <mat-form-field *ngIf="!invoice.plan" fxFlex>
        <mat-label>{{
          'payments.charge.number-months' | translate
        }}</mat-label>
        <mat-select formControlName="months" [disabled]="invoice.plan">
          <ng-container *ngFor="let month of months">
            <mat-option *ngIf="month != null" [value]="month"
              >{{ month }} {{ 'common.months' | translate }}</mat-option
            >
            <mat-divider *ngIf="month == null"></mat-divider>
          </ng-container>
        </mat-select>
      </mat-form-field>

      <button
        *ngIf="invoice.plan"
        fxFlex="15"
        type="submit"
        color="primary"
        mat-flat-button
        [disabled]="invoice.status === 'P' || isSaving || paymentPlanForm.invalid"
      >
        <mat-icon>autorenew</mat-icon>
        {{ 'payments.charge.update-card' | translate }}
      </button>
    </div>

    <div
      *ngIf="!invoice.plan"
      fxFlex
      fxLayout="row"
      fxLayoutGap="1rem"
      fxLayoutAlign="space-between center"
    >
      <mat-form-field fxFlex>
        <mat-label>{{
          'payments.charge.minimum-first-payment' | translate
        }}</mat-label>
        <input
          matInput
          formControlName="minPayment"
          type="text"
          [disabled]="invoice.plan"
        />
        <mat-hint>{{
          'payments.charge.minimum-first-payment-hint' | translate
        }}</mat-hint>
      </mat-form-field>

      <mat-form-field fxFlex>
        <mat-label>{{
          'payments.charge.financing-interest' | translate
        }}</mat-label>
        <input
          matInput
          formControlName="interest"
          type="number"
          min="0"
          max="30"
          step="0.01"
          [disabled]="invoice.plan"
        />
        <mat-hint>{{
          'payments.charge.financing-interest-hint' | translate
        }}</mat-hint>
      </mat-form-field>

      <mat-form-field fxFlex>
        <mat-label>{{
          'payments.charge.installment-fee' | translate
        }}</mat-label>
        <input
          matInput
          formControlName="installmentFee"
          type="number"
          min="0"
          max="30"
          step="0.01"
          [disabled]="invoice.plan"
        />
        <mat-hint>{{
          'payments.charge.installment-fee-hint' | translate
        }}</mat-hint>
      </mat-form-field>
    </div>

    <p class="text-center mt-6">
      <span *ngIf="paymentPlanForm.controls.months.value">
        {{ paymentPlanForm.controls.months.value }}
        {{ 'common.months' | translate }}
      </span>
      {{ 'payments.charge.financing-at' | translate }}
      <strong>{{ paymentPlanForm.controls.interest.value }}%</strong> APR +
      <strong>{{
        paymentPlanForm.controls.installmentFee.value | currency: invoice.currency
      }}</strong>
      {{ 'payments.charge.per-transaction' | translate }}
    </p>
    <hr width="50" class="mb-4" />
    <div class="text-center">
      <div *ngIf="!invoice.plan && invoicePlan" fxLayout="column">
        <span
          >{{ 'payments.charge.first-payment' | translate }}:
          <strong
            >{{ invoicePlan.firstPayment | currency: invoice.currency }}
            {{ 'common.now' | translate }}</strong
          ></span
        >
        <span
          >{{ 'payments.charge.other-months' | translate }}:
          <strong
            >{{
              invoicePlan.monthlyAmount | currency: invoice.currency
            }}
            / {{ 'common.month' | translate }}</strong
          ></span
        >
      </div>
      <div *ngIf="invoice.plan">
        <strong
          >{{ invoice.plan.monthlyAmount | currency }} /
          {{ 'common.months' | translate }}</strong
        ><br />
        <span *ngIf="invoice.plan.nextPaymentAt"
          >{{ 'payments.charge.next-payment' | translate }}
          {{ invoice.plan.nextPaymentAt | momentDate }}<br
        /></span>
        <span *ngIf="invoice.plan.endAt"
          >{{ 'payments.charge.last-payment' | translate }}
          {{ invoice.plan.endAt | momentDate }}</span
        >
      </div>
    </div>
  </mat-card-content>
  <mat-card-actions
    *ngIf="!invoice.plan"
    fxLayout="row"
    fxLayoutAlign="center center"
  >
    <button
      type="submit"
      mat-flat-button
      color="primary"
      fxFlex
      [disabled]="isSaving || invoice.isPayed"
    >
      {{ 'payments.charge.collect' | translate }}
      {{ invoicePlan.firstPayment | currency: invoice.currency }}
    </button>
  </mat-card-actions>
  <mat-card-actions
    *ngIf="invoice.plan"
    fxLayout="row"
    fxLayoutAlign="end center"
  >
    <button
      type="button"
      mat-flat-button
      color="warn"
      [disabled]="invoice.status === 'P' || isSaving"
      (click)="cancelPaymentPlan()"
    >
      {{ 'payments.charge.cancel-payment-plan' | translate }}
    </button>
  </mat-card-actions>
</form>
